import React from "react";
import { Button } from "@mui/material";
import { UnitResult, FloorplanResult } from "../types";
import {
  FloorplanRowHeader,
  FloorplanThumb,
  RowActions,
  RowTitle,
  StatCell,
  SpecialRibbon,
} from "../Availability.style";
import { FloorplanPlaceholder } from "./FloorplanPlaceholder";
import { currency, formatBedsBaths, shortDate } from "../format";

interface Props {
  unit: UnitResult;
  floorplan: FloorplanResult;
  onApply: (unit: UnitResult) => void;
  showThumb?: boolean;
  showRibbon?: boolean;
}

export const UnitInlineRow: React.FC<Props> = ({
  unit,
  floorplan,
  onApply,
  showThumb = true,
  showRibbon = false,
}) => {
  const photo =
    floorplan.images.find((i) => i.type === "Floorplan") ?? floorplan.images[0];
  const primary = unit.matchedTerms[0];
  const rent =
    unit.minEffectiveRent > 0 ? currency(unit.minEffectiveRent) : "—";

  return (
    <FloorplanRowHeader>
      {showThumb ? (
        <FloorplanThumb>
          {photo ? (
            <img src={photo.src} alt={photo.name ?? floorplan.name} />
          ) : (
            <FloorplanPlaceholder />
          )}
        </FloorplanThumb>
      ) : (
        <div />
      )}

      <RowTitle>
        <span className="title">
          {formatBedsBaths(unit.bedrooms, unit.bathrooms)}
        </span>
        <span className="subtitle">
          Unit {unit.unitNumber} · {unit.unitType}
        </span>
      </RowTitle>

      <StatCell>
        <span className="label">Base Rent</span>
        <span className="value">{rent}</span>
      </StatCell>
      <StatCell>
        <span className="label">Sq Ft</span>
        <span className="value">
          {unit.minSqft === unit.maxSqft
            ? unit.minSqft
            : `${unit.minSqft}–${unit.maxSqft}`}
        </span>
      </StatCell>
      <StatCell>
        <span className="label">Available</span>
        <span className="value">{shortDate(unit.madeReadyDate)}</span>
      </StatCell>
      <StatCell>
        <span className="label">Term</span>
        <span className="value">
          {primary ? `${primary.term} Months` : "—"}
        </span>
      </StatCell>
      <StatCell>
        <span className="label">Deposit</span>
        <span className="value">
          {unit.depositAmount > 0 ? currency(unit.depositAmount) : "—"}
        </span>
      </StatCell>
      <StatCell>
        <span className="label">Floor</span>
        <span className="value">{unit.floorLevel ?? "—"}</span>
      </StatCell>

      <RowActions>
        {showRibbon && unit.activeConcession && (
          <SpecialRibbon>SPECIAL</SpecialRibbon>
        )}
        <Button variant="contained" size="small" onClick={() => onApply(unit)}>
          Apply
        </Button>
      </RowActions>
    </FloorplanRowHeader>
  );
};
